import Localize from 'react-intl-universal';

import * as yup from 'yup';

import { MAIL_REGEX, PHONE_REGEX, ADDRESS_OPTIONS } from '@common/Constants';
import { euCountriesIds, AdditionalAddressTypes } from '@pages/BookingProcess/Constants';

const IS_LAYER = import.meta.env.VITE_CLIENT_BRAND === 'layher';

const additionalAddressValidation = (additionalAddressRadio, isFree) => {
  return yup.object().shape({
    firstName: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative,
      then: () => yup.string().required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    lastName: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative,
      then: () => yup.string().required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    mobile: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () =>
        yup
          .string()
          .matches(PHONE_REGEX, Localize.get('Validation.phoneFormat'))
          .min(9, Localize.get('Validation.short'))
          .nullable(true)
          .typeError(Localize.get('Validation.numberFilter')),
      otherwise: () => yup.string()
    }),
    phone: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () =>
        yup
          .string()
          .matches(PHONE_REGEX, Localize.get('Validation.phoneFormat'))
          .min(9, Localize.get('Validation.short'))
          .nullable(true)
          .typeError(Localize.get('Validation.numberFilter')),
      otherwise: () => yup.string()
    }),
    email: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative,
      then: () =>
        yup
          .string()
          .email(Localize.get('Validation.emailFilter'))
          .matches(MAIL_REGEX, Localize.get('Validation.emailFilter'))
          .nullable(true)
          .required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    street: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () => yup.string().required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    number: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () =>
        yup
          .string()
          .typeError(Localize.get('Validation.numberFilter'))
          .required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    city: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () => yup.string().required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    postCode: yup.string().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () =>
        yup
          .string()
          .typeError(Localize.get('Validation.numberFilter'))
          .required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string()
    }),
    country: yup.object().when(`$${additionalAddressRadio}`, {
      is: (radio) => radio === ADDRESS_OPTIONS.alternative && !isFree,
      then: () =>
        yup
          .object()
          .shape({ id: yup.number().required(), value: yup.string().required() })
          .required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.object().nullable(true)
    })
  });
};

export default (isParticipant = false, isFree = false) => {
  return yup.object().shape({
    generalInformation: yup.object().shape({
      firstName: yup.string().required(Localize.get('Validation.requiredField')),
      lastName: yup.string().required(Localize.get('Validation.requiredField')),
      birthDate: yup.date().required(Localize.get('Validation.requiredField')).nullable(true),
      company: yup.string().when([], {
        is: () => IS_LAYER === true,
        then: (schema) => schema.required(Localize.get('Validation.requiredField')),
        otherwise: (schema) => schema.notRequired()
      }),
      vatNo: yup
        .string()
        .when(['$contactInformation.country', '$billingAddress.country', '$billingAddressRadio'], {
          is: (contactCountry, billingCountry, billingAddressRadio) => {
            if (isParticipant) {
              return false;
            }
            return euCountriesIds.includes(
              (billingAddressRadio === ADDRESS_OPTIONS.alternative ? billingCountry?.id : 0) ||
                contactCountry?.id ||
                0
            );
          },
          then: () => yup.string().required(Localize.get('Validation.requiredField')),
          otherwise: () => yup.string()
        })
    }),
    hasIliasAccount: yup.boolean(),
    iliasUsername: yup.string().when('hasIliasAccount', {
      is: true,
      then: () => yup.string().required(Localize.get('Validation.requiredField')),
      otherwise: () => yup.string().notRequired()
    }),
    contactInformation: yup.object().shape({
      street: yup.string().when([], {
        is: () => isFree === true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(Localize.get('Validation.requiredField'))
      }),
      number: yup
        .string()
        .typeError(Localize.get('Validation.numberFilter'))
        .when([], {
          is: () => isFree === true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(Localize.get('Validation.requiredField'))
        }),
      city: yup.string().when([], {
        is: () => isFree === true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(Localize.get('Validation.requiredField'))
      }),
      postCode: yup
        .string()
        .typeError(Localize.get('Validation.numberFilter'))
        .when([], {
          is: () => isFree === true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(Localize.get('Validation.requiredField'))
        }),
      email: yup
        .string()
        .email(Localize.get('Validation.emailFilter'))
        .matches(MAIL_REGEX, Localize.get('Validation.emailFilter'))
        .nullable(true)
        .required(Localize.get('Validation.requiredField')),
      mobile: yup
        .string()
        .matches(PHONE_REGEX, Localize.get('Validation.phoneFormat'))
        .min(9, Localize.get('Validation.short'))
        .nullable(true)
        .typeError(Localize.get('Validation.numberFilter')),
      phone: yup
        .string()
        .matches(PHONE_REGEX, Localize.get('Validation.phoneFormat'))
        .min(9, Localize.get('Validation.short'))
        .typeError(Localize.get('Validation.numberFilter')),
      country: yup
        .object()
        .shape({ id: yup.number().required(), value: yup.string().required() })
        .when([], {
          is: () => isFree === true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(Localize.get('Validation.requiredField'))
        }),
      region: yup.object().shape({ id: yup.number(), value: yup.string() }).nullable(true)
    }),
    billingAddress: additionalAddressValidation(AdditionalAddressTypes.Billing.radioName),
    communicationAddress: additionalAddressValidation(
      AdditionalAddressTypes.Communication.radioName,
      isFree
    )
  });
};
